import { useMemo } from "react"
import { DashBoardConditions } from "features/dashBoard/types"
import { CommonGraphStyle, selectedItemStyle, filter } from "../../common"
 import { useSingleBarGraphOption } from "./options"
import { round } from "shared/util"
import { DownLoadData, DownLoadHeader } from "components/download/csv/types"

interface BarSeries {
  code: string
  name?: string
  value: number
  count: number
  itemStyle?: CommonGraphStyle
}

type useSingleBarGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useSingleBarGraphOption<T>>
}
export const useSingleBarGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
    axisDef, valueDef,
    formula, sort
  }
}: useSingleBarGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = conditions[axisDef.key]

		const stackFunc = axisDef ? axisDef.val : configure.stack ? configure.stack : undefined
		const series: BarSeries[] = []
		for (const record of dataList) {
			const value = valueDef.val(record)
			if (value === undefined || value === 0) continue
			if (!filter(record, configure, conditions, axisDef.key, targetList)) continue

			const stack = stackFunc ? stackFunc(record) : { code: '***', name: '' }
			if (!stack) continue
			const stackList = Array.isArray(stack) ? stack : [stack]
			for (const stack of stackList) {
				if (filterText && stack.name) {
					const stackName = stack.name
					if (filterText.split(' ').every((t) => stackName.indexOf(t) < 0)) continue
				}
				let its = series.find((s) => s.code === stack.code)
				if (!its) {
					its = {
						code: stack.code,
						name: stack.name,
						value: value,
						count: 1,
						itemStyle: selectedItemStyle(condition, stack)
					}
					series.push(its)
				} else {
					if (formula === 'max') {
						if (its.value < value) its.value = value
					} else if (formula === 'min') {
						if (its.value > value) its.value = value
					} else {
						its.value = its.value + value
					}
					its.count = its.count + 1
				}
			}
		}
		if (formula === 'ave') {
			series.forEach((r) => r.value = r.value / r.count)
		}
		if (sort) {
			series.sort((a, b) => b.value - a.value)
		} else {
			series.sort((a, b) => a.code.localeCompare(b.code))
		}
		if (series.length === 0) series.push({ code: '**', name: '', value: 0, count: 0 })

		const total = series.reduce((total, series) => total + series.value, 0)

		return {
			series,
			total,
			csvBuilder: (): DownLoadData | undefined => {
				if (total <= 0) return undefined
				if (!axisDef?.key || !valueDef?.key) return undefined
	
				const axisKey = axisDef.key
				const valueKey = valueDef.key
				const unit = valueDef.unit
	
				const header: DownLoadHeader[] = [
					{ title: axisDef.title, key: axisKey },
					{ title: valueDef.title, key: valueKey, unit },
					{ title: '割合', key: 'percentage', unit: '%' }
				]
				const body = series.map((series) => ({
					[axisKey]: series.name,
					[valueKey]: series.value,
					percentage: round((series.value * 100) / total, 1)
				}))
				return { header, body }
			}	
		}
	},
	[dataList, conditions, configure, axisDef, valueDef, formula, sort, filterText, targetList]
)

